// ─── Options des champs cliniques structurés ────────────────────────────
//
// Catalogue partagé, créé le 2026-07-31. Les valeurs sont IDENTIQUES à
// celles du questionnaire cliente (anissa-client-app,
// lib/questionnaire-schema.ts, section « sante_medicale »).
//
// POURQUOI CE FICHIER : les deux surfaces vivent dans deux dépôts. Si la
// fiche praticienne écrivait « Crohn » et le questionnaire « crohn », la
// résolution clinique verrait deux choses différentes et un blocage
// pourrait ne pas se déclencher. Le test de cohérence
// (__tests__/clinicalFieldOptions.test.js) verrouille l'alignement avec
// MICI_VALEURS / THYRO_A_STABILISER.
//
// Les libellés sont ceux validés par Anissa, au vouvoiement côté
// praticienne (elle lit sa propre fiche) et au tutoiement côté cliente.
// Seules les VALEURS doivent rester identiques entre les deux.

import { basculerDigestif } from './digestifSelection.js';

/** Réponse explicite « je ne sais pas » — distincte d'une absence de réponse. */
export const JE_NE_SAIS_PAS = 'jeNeSaisPas';

export const DIABETE_OPTIONS = Object.freeze([
  { value: 'aucun', label: 'Aucun diagnostic' },
  { value: 'type1', label: 'Diabète de type 1' },
  { value: 'type2', label: 'Diabète de type 2' },
  { value: 'gestationnel', label: 'Diabète gestationnel actuel' },
  // 2026-08-01, arbitrage 3 d'Anissa. L'ancien libellé « Prédiabète ou
  // insulinorésistance » réunissait les deux sous une seule valeur : elle a
  // demandé qu'ils soient DISTINCTS, et que l'insulinorésistance ne soit pas
  // assimilée automatiquement au prédiabète.
  { value: 'prediabete', label: 'Prédiabète' },
  { value: 'insulinoresistance', label: 'Insulinorésistance diagnostiquée' },
  { value: 'autre', label: 'Autre' },
  { value: JE_NE_SAIS_PAS, label: 'Je ne sais pas' },
]);

/**
 * 2026-08-01, arbitrage 4 d'Anissa : ce champ est à RÉPONSES MULTIPLES.
 * Un SII et un RGO coexistent fréquemment ; la réponse unique forçait la
 * cliente à en taire un. « Aucun diagnostic » reste exclusif à la saisie.
 */
export const DIGESTIF_MULTIPLE = true;

/**
 * Bascule une valeur dans un champ à réponses multiples.
 *
 * Extraite du composant pour être TESTABLE : c'est ici qu'une ancienne
 * réponse scalaire pourrait disparaître sans bruit. Une fiche portant
 * encore `'crohn'` et sur laquelle Anissa coche « SII » doit ressortir avec
 * les DEUX, jamais avec le seul dernier coché.
 *
 * « Aucun diagnostic » est exclusif : le cocher efface les autres, et
 * cocher un diagnostic l'efface.
 *
 * @param {string|string[]|undefined} valeurActuelle - scalaire ou tableau
 * @returns {string[]} nouvelle liste, l'entrée n'est jamais mutée
 */
export function basculerValeurMultiple(valeurActuelle, valeur) {
  // Délègue à la source unique. Les règles d'exclusivité (« aucun » ET
  // « je ne sais pas ») vivent dans un seul fichier, partagé avec la
  // fonction serveur — sinon les trois points de saisie divergent.
  return basculerDigestif(valeurActuelle, valeur);
}

export const DIGESTIF_OPTIONS = Object.freeze([
  { value: 'aucun', label: 'Aucun diagnostic' },
  { value: 'sii', label: "Syndrome de l'intestin irritable (SII)" },
  { value: 'rgo', label: 'Reflux gastro-œsophagien (RGO)' },
  { value: 'crohn', label: 'Maladie de Crohn' },
  { value: 'rch', label: 'Rectocolite hémorragique' },
  { value: 'autreMici', label: 'Autre MICI' },
  { value: 'autre', label: 'Autre' },
  { value: JE_NE_SAIS_PAS, label: 'Je ne sais pas' },
]);

export const MICI_STATUT_OPTIONS = Object.freeze([
  { value: 'remission', label: 'En rémission' },
  { value: 'active', label: 'Active ou en poussée' },
  { value: JE_NE_SAIS_PAS, label: 'Je ne sais pas' },
]);

export const THYROIDE_OPTIONS = Object.freeze([
  { value: 'aucun', label: 'Aucun diagnostic' },
  { value: 'hypothyroidie', label: 'Hypothyroïdie' },
  { value: 'hashimoto', label: 'Hashimoto' },
  { value: 'hyperthyroidie', label: 'Hyperthyroïdie' },
  { value: 'basedow', label: 'Maladie de Basedow' },
  { value: 'autre', label: 'Autre' },
  { value: JE_NE_SAIS_PAS, label: 'Je ne sais pas' },
]);

export const THYROIDE_STATUT_OPTIONS = Object.freeze([
  { value: 'oui', label: 'Oui, situation considérée comme stabilisée' },
  { value: 'non', label: 'Non, situation non stabilisée' },
  { value: JE_NE_SAIS_PAS, label: 'Je ne sais pas' },
]);

export const TRAITEMENTS_OPTIONS = Object.freeze([
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
]);

/** Libellé lisible d'une valeur, ou « Non renseigné » si absente. */
export function libelleOption(options, valeur) {
  if (!valeur) return 'Non renseigné';
  const match = options.find((o) => o.value.toLowerCase() === String(valeur).toLowerCase());
  return match ? match.label : String(valeur);
}
